import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import "./styles.css";

const PIN_SVG_INNER = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="36" height="36"><path d="M528 32C325.056 32 160 196.8 160 399.36c0 75.2 22.656 147.584 65.024 208.48 2.112 3.648 4.256 7.168 6.784 10.592l268.608 353.472c7.296 8.096 17.088 12.576 27.584 12.576 10.368 0 20.224-4.512 28.768-14.08l267.36-352c2.624-3.52 4.896-7.36 6.112-9.6A364.864 364.864 0 0 0 896 399.36C896 196.8 730.912 32 528 32z m0 498.72a131.52 131.52 0 0 1-131.456-131.232 131.488 131.488 0 0 1 262.88 0 131.52 131.52 0 0 1-131.424 131.2z" fill="#2d79e9"/></svg>`;

const PIN_SVG_SMALL = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="28" height="28"><path d="M528 32C325.056 32 160 196.8 160 399.36c0 75.2 22.656 147.584 65.024 208.48 2.112 3.648 4.256 7.168 6.784 10.592l268.608 353.472c7.296 8.096 17.088 12.576 27.584 12.576 10.368 0 20.224-4.512 28.768-14.08l267.36-352c2.624-3.52 4.896-7.36 6.112-9.6A364.864 364.864 0 0 0 896 399.36C896 196.8 730.912 32 528 32z m0 498.72a131.52 131.52 0 0 1-131.456-131.232 131.488 131.488 0 0 1 262.88 0 131.52 131.52 0 0 1-131.424 131.2z" fill="#2d79e9"/></svg>`;

const _GCJ02_A = 6378245.0;
const _GCJ02_EE = 0.00669342162296594323;

function _gcjOutOfChina(lng, lat) {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
}

function _gcjTransformLat(lng, lat) {
  let ret = -100.0 + 2.0 * lng + 3.0 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng));
  ret += ((20.0 * Math.sin(6.0 * lng * Math.PI) + 20.0 * Math.sin(2.0 * lng * Math.PI)) * 2.0) / 3.0;
  ret += ((20.0 * Math.sin(lat * Math.PI) + 40.0 * Math.sin((lat / 3.0) * Math.PI)) * 2.0) / 3.0;
  ret += ((160.0 * Math.sin((lat / 12.0) * Math.PI) + 320 * Math.sin((lat * Math.PI) / 30.0)) * 2.0) / 3.0;
  return ret;
}

function _gcjTransformLng(lng, lat) {
  let ret = 300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng));
  ret += ((20.0 * Math.sin(6.0 * lng * Math.PI) + 20.0 * Math.sin(2.0 * lng * Math.PI)) * 2.0) / 3.0;
  ret += ((20.0 * Math.sin(lng * Math.PI) + 40.0 * Math.sin((lng / 3.0) * Math.PI)) * 2.0) / 3.0;
  ret += ((150.0 * Math.sin((lng / 12.0) * Math.PI) + 300.0 * Math.sin((lng / 30.0) * Math.PI)) * 2.0) / 3.0;
  return ret;
}

export const wgs84ToGcj02 = (lng, lat) => {
  const lngN = Number(lng);
  const latN = Number(lat);
  if (!Number.isFinite(lngN) || !Number.isFinite(latN)) return null;
  if (_gcjOutOfChina(lngN, latN)) return { lng: lngN, lat: latN };
  let dLat = _gcjTransformLat(lngN - 105.0, latN - 35.0);
  let dLng = _gcjTransformLng(lngN - 105.0, latN - 35.0);
  const radLat = (latN / 180.0) * Math.PI;
  let magic = Math.sin(radLat);
  magic = 1 - _GCJ02_EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  dLat = (dLat * 180.0) / (((_GCJ02_A * (1 - _GCJ02_EE)) / (magic * sqrtMagic)) * Math.PI);
  dLng = (dLng * 180.0) / ((_GCJ02_A / sqrtMagic) * Math.cos(radLat) * Math.PI);
  return { lng: lngN + dLng, lat: latN + dLat };
};

function escapeHtml(s) {
  if (!s) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function getPoiLocation(poi) {
  if (poi.lng && poi.lat) return wgs84ToGcj02(poi.lng, poi.lat);
  const loc = poi.location || poi.detail?.location;
  if (loc && typeof loc === "string") {
    const [lng, lat] = loc.split(",").map(Number);
    if (!isNaN(lng) && !isNaN(lat)) return wgs84ToGcj02(lng, lat);
  }
  return null;
}

function getDetailField(poi, field) {
  return poi[field] || poi.detail?.[field] || "";
}

function parseCenter(center) {
  if (!center) return [116.397428, 39.90923];
  if (typeof center === "string") return center.split(",").map(Number);
  return center;
}

function isPoisUrl(pois) {
  return typeof pois === "string" && /^https?:\/\//i.test(pois.trim());
}

function normalizePois(pois) {
  if (Array.isArray(pois)) return pois;
  if (!pois) return [];

  if (typeof pois === "string") {
    const trimmed = pois.trim();
    if (!trimmed) return [];
    try {
      return normalizePois(JSON.parse(trimmed));
    } catch {
      return [];
    }
  }

  if (typeof pois === "object") {
    if (Array.isArray(pois.pois)) return pois.pois;
    if (Array.isArray(pois.data)) return pois.data;
    if (Array.isArray(pois.results)) return pois.results;
    if (pois.name || pois.location || pois.detail || pois.lng || pois.lat) return [pois];

    const values = Object.values(pois).filter(
      (item) => item && typeof item === "object" && !Array.isArray(item)
    );
    return values.filter((item) => item.name || item.location || item.detail || item.lng || item.lat);
  }

  return [];
}

const PoiList = ({ pois, keywords }) => {
  if (!pois.length) return null;

  return (
    <div className="amap-poi-list">
      <div className="amap-poi-list-header">
        <span>{keywords ? `附近${keywords}` : "附近地点"}</span>
        <span className="amap-poi-list-count">{pois.length}个地点</span>
      </div>
      <div className="amap-poi-list-body">
        {pois.map((poi, index) => {
          const rating = getDetailField(poi, "rating");
          const type = getDetailField(poi, "type");
          const typeName = type ? type.split(";").pop() : "";
          const address = poi.address || poi.detail?.address || "";
          const key = poi.id || poi.detail?.id || `${poi.name || "poi"}-${index}`;

          return (
            <div className="amap-poi-item" key={key}>
              <div className="amap-poi-index">{index + 1}</div>
              <div className="amap-poi-content">
                <div className="amap-poi-name">{poi.name || poi.detail?.name || "未命名地点"}</div>
                {(rating || typeName) && (
                  <div className="amap-poi-meta">
                    {rating && <span className="amap-poi-rating">★ {rating}</span>}
                    {typeName && <span>{typeName}</span>}
                  </div>
                )}
                {address && <div className="amap-poi-address">{address}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function deduplicatePois(pois) {
  const seen = new Set();
  return pois.filter((poi) => {
    const id = poi.id || poi.detail?.id;
    const pos = getPoiLocation(poi);
    const key = id || (pos ? `${pos.lng.toFixed(6)},${pos.lat.toFixed(6)}` : null);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const OFFSET_THRESHOLD = 0.0001;
function spreadOverlapping(positions) {
  const result = [...positions];
  for (let i = 0; i < result.length; i++) {
    for (let j = i + 1; j < result.length; j++) {
      const dx = Math.abs(result[i][0] - result[j][0]);
      const dy = Math.abs(result[i][1] - result[j][1]);
      if (dx < OFFSET_THRESHOLD && dy < OFFSET_THRESHOLD) {
        const angle = (2 * Math.PI * j) / result.length;
        result[j] = [
          result[j][0] + Math.cos(angle) * 0.0003,
          result[j][1] + Math.sin(angle) * 0.0003,
        ];
      }
    }
  }
  return result;
}

function addMarkersToMap(map, pois, centerCoords, { small = false } = {}) {
  const svgIcon = small ? PIN_SVG_SMALL : PIN_SVG_INNER;
  const nameClass = small ? "amap-pin-marker amap-pin-marker--small" : "amap-pin-marker";
  const curClass = small
    ? "amap-pin-marker amap-pin-marker--small amap-pin-marker--current"
    : "amap-pin-marker amap-pin-marker--current";

  const uniquePois = deduplicatePois(pois);

  const rawPositions = uniquePois.map((poi) => {
    const pos = getPoiLocation(poi);
    return pos ? [pos.lng, pos.lat] : null;
  });
  const validIndices = rawPositions.map((p, i) => (p ? i : -1)).filter((i) => i >= 0);
  const validPositions = validIndices.map((i) => rawPositions[i]);
  const spread = spreadOverlapping(validPositions);

  validIndices.forEach((poiIdx, spreadIdx) => {
    const poi = uniquePois[poiIdx];
    const [lng, lat] = spread[spreadIdx];

    const el = document.createElement("div");
    el.className = nameClass;
    el.innerHTML = `
      <div class="amap-pin-marker-name">${escapeHtml(poi.name)}</div>
      <div class="amap-pin-marker-icon">${svgIcon}</div>
    `;

    const marker = new window.AMap.Marker({
      position: [lng, lat],
      content: el,
      anchor: "bottom-center",
      offset: new window.AMap.Pixel(0, 0),
    });

    if (!small) {
      const rating = getDetailField(poi, "rating");
      const type = getDetailField(poi, "type");
      const addr = poi.address || "";
      const photo = getDetailField(poi, "photo");

      const infoContent = `
        <div class="amap-custom-info">
          <div class="amap-custom-info-close">&times;</div>
          <div class="amap-custom-info-body">
            <div class="amap-custom-info-title">${escapeHtml(poi.name)}</div>
            ${rating ? `<div class="amap-custom-info-rating">★ ${escapeHtml(rating)}</div>` : ""}
            ${type ? `<div class="amap-custom-info-type">${escapeHtml(type.split(";").pop())}</div>` : ""}
            ${addr ? `<div class="amap-custom-info-addr">${escapeHtml(addr)}</div>` : ""}
            ${photo ? `<img class="amap-custom-info-photo" src="${escapeHtml(photo)}" alt="" />` : ""}
          </div>
          <div class="amap-custom-info-arrow"></div>
        </div>
      `;
      const infoWindow = new window.AMap.InfoWindow({
        content: infoContent,
        isCustom: true,
        offset: new window.AMap.Pixel(0, -42),
        anchor: "bottom-center",
      });
      marker.on("click", () => {
        infoWindow.open(map, [lng, lat]);
      });
    }

    map.add(marker);
  });

  const curEl = document.createElement("div");
  curEl.className = curClass;
  curEl.innerHTML = `
    <div class="amap-pin-marker-name">当前位置</div>
    <div class="amap-pin-marker-icon">${svgIcon}</div>
  `;
  const currentMarker = new window.AMap.Marker({
    position: centerCoords,
    content: curEl,
    anchor: "bottom-center",
    offset: new window.AMap.Pixel(0, 0),
  });
  map.add(currentMarker);
}

function fitMapBounds(map, pois, centerCoords) {
  const allPoints = pois
    .map(getPoiLocation)
    .filter(Boolean)
    .map((p) => [p.lng, p.lat]);
  allPoints.push(centerCoords);
  if (allPoints.length > 1) {
    map.setBounds(
      new window.AMap.Bounds(
        [
          Math.min(...allPoints.map((p) => p[0])) - 0.005,
          Math.min(...allPoints.map((p) => p[1])) - 0.003,
        ],
        [
          Math.max(...allPoints.map((p) => p[0])) + 0.005,
          Math.max(...allPoints.map((p) => p[1])) + 0.003,
        ]
      )
    );
  }
}

const AmapMiniMap = React.memo(({ pois, center, onClick }) => {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [ready, setReady] = useState(false);

  const initMap = useCallback(() => {
    if (!containerRef.current || !window.AMap || !center || mapRef.current) return;

    const centerCoords = parseCenter(center);
    const map = new window.AMap.Map(containerRef.current, {
      zoom: 14,
      center: centerCoords,
      mapStyle: "amap://styles/normal",
      dragEnable: false,
      zoomEnable: false,
      touchZoom: false,
      doubleClickZoom: false,
      scrollWheel: false,
      keyboardEnable: false,
      showIndoorMap: false,
    });

    map.on("complete", () => setReady(true));
    mapRef.current = map;
    addMarkersToMap(map, pois, centerCoords, { small: true });
    fitMapBounds(map, pois, centerCoords);
  }, [pois, center]);

  const destroyMap = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.destroy();
      mapRef.current = null;
      setReady(false);
    }
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          initMap();
        } else {
          destroyMap();
        }
      },
      { rootMargin: "800px 0px" }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      destroyMap();
    };
  }, [initMap, destroyMap]);

  return (
    <div className="amap-card-map" ref={wrapperRef}>
      {!ready && (
        <div className="amap-card-map-placeholder">
          <div className="amap-card-map-placeholder-pulse" />
        </div>
      )}
      <div className="amap-card-map-inner" ref={containerRef} />
      <div className="amap-card-map-touch" onClick={onClick} />
      <div className="amap-card-map-expand">点击查看大地图</div>
    </div>
  );
});

const AmapFullMap = ({ pois, center, keywords, onClose }) => {
  const mapContainerRef = useRef(null);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!mapContainerRef.current || !window.AMap || !center) return;

    const centerCoords = parseCenter(center);

    const map = new window.AMap.Map(mapContainerRef.current, {
      zoom: 14,
      center: centerCoords,
      mapStyle: "amap://styles/normal",
    });

    addMarkersToMap(map, pois, centerCoords, { small: false });
    fitMapBounds(map, pois, centerCoords);

    const container = mapContainerRef.current;
    const handleInfoClose = (e) => {
      if (e.target.closest(".amap-custom-info-close")) {
        e.stopPropagation();
        map.clearInfoWindow();
      }
    };
    container.addEventListener("click", handleInfoClose);

    return () => {
      container.removeEventListener("click", handleInfoClose);
      map.destroy();
    };
  }, [pois, center]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose(), 300);
  }, [onClose]);

  return (
    <div className={`amap-fullmap-overlay${closing ? " amap-fullmap-closing" : ""}`} onClick={handleClose}>
      <div className={`amap-fullmap-container${closing ? " amap-fullmap-container-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="amap-fullmap-header">
          <span className="amap-fullmap-title">
            {keywords ? `附近${keywords}` : "地点详情"}
          </span>
          <button type="button" className="amap-fullmap-close" onClick={handleClose}>
            ✕
          </button>
        </div>
        <div className="amap-fullmap-body" ref={mapContainerRef} />
      </div>
    </div>
  );
};

const AmapCard = ({ pois: wsPois, location: wsLocation, keywords: wsKeywords, userLocation: parentLocation }) => {
  const [showFullMap, setShowFullMap] = useState(false);
  const [remotePois, setRemotePois] = useState(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const localPois = useMemo(() => normalizePois(wsPois), [wsPois]);
  const pois = remotePois || localPois;

  useEffect(() => {
    if (!isPoisUrl(wsPois)) {
      setRemotePois(null);
      setRemoteLoading(false);
      return;
    }

    const controller = new AbortController();
    const url = wsPois.trim();
    setRemotePois(null);
    setRemoteLoading(true);

    fetch(url, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch pois: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setRemotePois(normalizePois(data));
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Failed to load map pois:", err);
          setRemotePois([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setRemoteLoading(false);
      });

    return () => controller.abort();
  }, [wsPois]);

  const fallbackCenter = wsLocation || (pois.length > 0 ? pois[0]?.location : "");
  const userLocation = parentLocation || fallbackCenter;

  const openFullMap = useCallback(() => setShowFullMap(true), []);
  const closeFullMap = useCallback(() => setShowFullMap(false), []);

  const detailPois = useMemo(() => {
    if (pois.length === 0) return [];
    const withDetail = pois.filter((p) => p.detail && Object.keys(p.detail).length > 0);
    return withDetail.length > 0 ? withDetail : pois.slice(0, 5);
  }, [pois]);

  if (pois.length === 0) {
    if (!remoteLoading) return null;

    return (
      <div className="amap-card">
        <div className="amap-card-loading">
          <span className="amap-loading-dot" />
          <span>正在加载附近地点...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="amap-card">
        {userLocation && (
          <AmapMiniMap
            pois={detailPois}
            center={userLocation}
            onClick={openFullMap}
          />
        )}
        <PoiList pois={detailPois} keywords={wsKeywords} />
      </div>

      {showFullMap &&
        typeof document !== "undefined" &&
        createPortal(
          <AmapFullMap
            pois={detailPois}
            center={userLocation}
            keywords={wsKeywords}
            onClose={closeFullMap}
          />,
          document.body
        )}
    </>
  );
};

export default AmapCard;
